import { forwardRef, type ReactNode } from 'react';
import { blue, red, slate, white } from '@/lib/colours';
import {
  CircularProgress,
  FormControl,
  HelperText,
  InputLabel,
  Select,
  type FormControlProps,
  type SelectProps,
} from '@/primitives';
import { EventType } from '@/lib/types';

export interface SelectFieldProps extends SelectProps {
  fullWidth?: boolean;
  label: ReactNode;
  labelId: string;
  loading?: boolean;
  sx?: FormControlProps['sx'];
  variant?: SelectProps['variant'];
  helperText?: string[];
  trackSelect?: (event: any) => void;
  trackEventName?: string;
  eventType?: EventType;
}

function LoadingIndicator() {
  return <CircularProgress variant="indeterminate" size={24} />;
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  function SelectField(
    {
      fullWidth,
      label,
      labelId,
      loading,
      sx,
      variant,
      helperText,
      trackSelect,
      trackEventName,
      eventType = EventType.DROPDOWN_SELECT,
      onChange,
      ...props
    },
    ref,
  ) {
    const handleChange: SelectFieldProps['onChange'] = (event, child) => {
      if (trackSelect && trackEventName) {
        trackSelect({
          name: trackEventName,
          properties: {
            eventType,
            fieldName: labelId,
            selectedValue: event.target.value,
          },
        });
      }
      if (onChange) {
        onChange(event, child);
      }
    };

    if (variant === 'filled') {
      return (
        <FormControl fullWidth={fullWidth} sx={sx}>
          <InputLabel
            id={labelId}
            required={props.disabled ? false : props.required}
            shrink
            variant="filled"
            error={!!helperText}
            sx={{
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: '500',
              lineHeight: '20px',
              letterSpacing: '0.25px',
              paddingTop: '3px',
              paddingLeft: '1px',
              ':has(+ .Mui-disabled)': {
                color: 'rgba(0, 0, 0, 0.38)',
              },
            }}
          >
            {label}
          </InputLabel>
          <Select
            {...props}
            disableUnderline
            IconComponent={loading ? LoadingIndicator : undefined}
            labelId={labelId}
            ref={ref}
            slotProps={{
              input: {
                sx: {
                  backgroundColor: white[900],
                  borderRadius: '8px',
                  border: `1px solid ${
                    helperText == undefined ? slate[300] : red[600]
                  }`,
                  ':focus-within': {
                    borderColor: blue[600],
                  },
                  ':has(input[aria-invalid="true"])': {
                    borderColor: red[600],
                  },
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: '400',
                  lineHeight: '20px',
                  letterSpacing: '0.25px',
                  '&.Mui-disabled': {
                    border: `1px solid ${slate[300]}`,
                    borderRadius: '8px',
                    fontStyle: 'italic',
                  },
                },
              },
            }}
            sx={{
              borderRadius: '8px',
              '.MuiSelect-select': {
                borderRadius: '8px !important',
              },
            }}
            variant="filled"
            onChange={handleChange}
          />
          <HelperText helperText={helperText} sx={{ color: red[600] }} />
        </FormControl>
      );
    }

    return (
      <FormControl sx={sx}>
        <InputLabel id={labelId} variant={variant}>
          {label}
        </InputLabel>
        <Select
          {...props}
          labelId={labelId}
          ref={ref}
          variant={variant}
          onChange={handleChange}
        />
        <HelperText helperText={helperText} sx={{ color: red[600] }} />
      </FormControl>
    );
  },
);
